import { TMediaContent } from "components/Fixtures/Fixtures.types";
import { useEffect, useState } from "react";
import Slider from "react-slick";

type TMediaCarouselProps = {
  items: TMediaContent[];
};

const renderMedia = (
  media: TMediaContent,
  showControls = true,
  isLoadedInModal = false
) => {
  if (media.MediaType === "image") {
    return <img className="m-carousel-modal__image" src={media.ImageUrl} alt={media.Title} />;
  }

  if (media.MediaType === "video") {
    return (
      <video
        style={{ objectFit: "cover", width: "100%" }}
        poster={media.CoverImageSrc}
        id={`media-content-video - ${media.VideoSrc}${
          isLoadedInModal ? "-modal" : ""
        }`}
        width="100%"
        height="100%"
        controls={showControls}
      >
        <source src={media.VideoSrc} type="video/mp4"  />
      </video>
    );
  }

  if (media.MediaType === "iframe") {
    return (
      <div className="m-carousel-modal__iframe">
      <iframe
        width="100%"
        height="100%"
        src={media.IFrame}
        frameBorder="0"
        title={media.Title}
      ></iframe>
      </div>
    );
  }

  return <></>;
};

function renderTextBasedOnWidth(text: string) {
  const screenWidth = window.innerWidth;
  var maxChars = 63;
  if (screenWidth < 400) {
    maxChars = 30;
  } else if (screenWidth < 600) {
    maxChars = 35;
  } else if (screenWidth < 1024) {
    maxChars = 50;
  }
  
  if (text.length > maxChars) {
    return text.substring(0, maxChars) + "...";
  }
  return text;
}

function MediaCarousel({ items }: TMediaCarouselProps) {
  const [activeIndex, setCurrentActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const hasDisabledArrows = items.length === 1;

  const activeMedia = items?.[activeIndex];

  const setActiveIndex = (index: number) => {
    if (!activeMedia) {
      setCurrentActiveIndex(0);
      return;
    }

    if (activeMedia.MediaType === "video") {
      const videoElement = document.getElementById(
        `media-content-video - ${activeMedia.VideoSrc}`
      ) as HTMLVideoElement;

      if (videoElement) {
        videoElement.pause();
        videoElement.removeAttribute("src");
        videoElement.currentTime = 0;
        videoElement.load();
      }

      setCurrentActiveIndex(index);
    } else {
      setCurrentActiveIndex(index);
    }
  };

  useEffect(() => {
    setActiveIndex(0);

    // eslint-disable-next-line
  }, [items]);

  if (!items.length) {
    return <></>;
  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    // display at most 5 slides, but if there are only 3 items in the gallery, display only 3
    slidesToShow: Math.min(items?.length ?? 0, 5),
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "0",
  };

  const toggleModal = (value: boolean) => {
    console.log("toggleModal", value);
    setShowModal(value);
  };

  return (
    <div>
      <div className="o-facility-content__image-carousel">
        <div className="o-facility-content__image-carousel-image">
          {activeMedia && renderMedia(activeMedia)}
        </div>

        <div
          className="o-facility-content__image-carousel-footer"
          style={{
            ...(hasDisabledArrows ? { justifyContent: "center" } : {}),
          }}
        >
          {(() => {
              const rawText = `${activeIndex + 1}/${items.length}${
                activeMedia?.Title ? ` - ${activeMedia?.Title}` : ""
              }`;
              const shortened = renderTextBasedOnWidth(rawText);
              const [numberPart, ...rest] = shortened.split(" - ");
              const titlePart = rest.join(" - ");
              
              return (
                <span>
                  <span className="number">
                    {numberPart}
                    {titlePart && ` -`}
                  </span>
                  {titlePart && ` ${titlePart}`}
                </span>
              );
            })()}
          <div
            className="o-media-carousel__open-wrap"
          >
            <button
              className="o-media-carousel__open"
              onClick={() => toggleModal(true)}
            >
              <i className="zmdi zmdi-crop-free"></i>
            </button>
          </div>
        </div>
      </div>

      <div
        id="js-imageModal"
        className="m-carousel-modal"
        style={{ display: showModal ? "block" : "none" }}
      >
        {showModal && (
          <div className="m-carousel-modal__content">
            <button
              className="m-carousel-modal__close"
              onClick={() => toggleModal(false)}
            >
              <i
                className="zmdi zmdi-close-circle-o"
                style={{ top: "0px", left: "0px" }}
              ></i>
            </button>
            {showModal && renderMedia(activeMedia, true, true)}
          </div>
        )}
      </div>

      {items?.length > 1 && (
        <Slider
          className="o-facility-content__slider"
          {...sliderSettings}
          nextArrow={
            <div>
              <i className="zmdi zmdi-long-arrow-right" />
            </div>
          }
          prevArrow={
            <div>
              <i className="zmdi zmdi-long-arrow-left" />
            </div>
          }
        >
          {(items || []).map((item, index) => (
            <div
              onClick={() => setActiveIndex(index)}
              className={`${
                item.MediaType === "video" ? "has-video-play" : ""
              }`}
            >
              {renderMedia(item, false)}
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
}

export default MediaCarousel;
